import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import AbacusClientUtils from '@homeaway/abacus-client-utils';
import determineIfMobile from '@homeaway/react-determine-if-mobile';

import {ListYourProperty} from './list-your-property/ListYourProperty';
import {Logo} from './logo/Logo';
import {Nav} from './nav/Nav';
import {Tray} from './tray/Tray';
import Feedback from '@homeaway/react-feedback';
import {EdapProvider} from '@homeaway/react-event-tracking';
import {NotificationBar} from './notification-bar/NotificationBar';
import {edapTrigger} from '../utils/triggerEdap';
import {linkLocations, namedEvents} from '../utils/edap';

import './SiteHeader.less';

// eslint-disable-next-line complexity
function SiteHeader({
    accessibility,
    bgWhite,
    children,
    currencyCode,
    currencySelector,
    defaultAvatar,
    displaySignUpLink,
    displayTrayJewel,
    displayNotificationBar,
    extraNav,
    features,
    feedbackComponentLingua,
    flags,
    fullLocale,
    globalizationSelector,
    handleNotificationBarDismiss,
    handleNotificationMessageDismiss,
    help,
    internalTools,
    isInverse,
    isMobile,
    isTrayOpen,
    localeSwitcher,
    localeSwitcherOverrides,
    login,
    logo,
    logoutRedirectUrl,
    lyp,
    mobileApp,
    notificationMessages,
    onGlobalSelectorChange,
    onTrayChange,
    partnerLogo,
    roadTrips,
    clientRoadTrips,
    scratchpad,
    showRoadTrips,
    signUp,
    siteName,
    sitePartnerLogo,
    theme,
    traveler,
    travelerInbox,
    travelerLoginClickHandler,
    tripboard,
    withoutAccessibility,
    withoutCurrencySelector,
    withoutFlags,
    withoutHelpLinks,
    withoutInbox,
    withoutListYourProperty,
    withoutLocaleSwitcher,
    withoutLogin,
    withoutNav,
    withoutScratchpad,
    withoutSignUp,
    withoutTraveler,
    withoutTray
}) {
    const hasFeatureSearch = !!features && !!features.search;
    const [hasSearch] = useState(hasFeatureSearch);
    const [trayOpen, setTrayOpen] = useState(isTrayOpen);

    useEffect(() => {
        if (window && window.__ABACUS__) {
            let experimentNames = ['HA_GX_COUNTRY_LANGUAGE_SELECTOR', 'HA_GX_COUNTRY_LANGUAGE_SELECTOR_NL', 'VRBO_SAVE_CURRENCY'];

            for (let experimentName of experimentNames) {
                // @ts-ignore
                if (window.__ABACUS__[experimentName]) {
                    AbacusClientUtils.evaluateExperimentAndLog(experimentName);
                }
            }
        }
    }, []);

    useEffect(() => {
        let body = document.querySelector('body');

        if (trayOpen) {
            body.classList.add('site-header-tray-open', 'site-header-tray-fixed');
        } else {
            body.classList.remove('site-header-tray-open');
            setTimeout(() => body.classList.remove('site-header-tray-fixed'), 500);
        }
        onTrayChange(trayOpen);
    }, [trayOpen]);

    useEffect(() => {
        setTrayOpen(isTrayOpen);
    }, [isTrayOpen]);

    function determineAvatar(travelerCopy) {
        if (travelerCopy && travelerCopy.avatar && !travelerCopy.avatar.image) {
            travelerCopy.avatar.image = defaultAvatar;
        }

        return travelerCopy;
    }

    function handleToggleOpenTray() {
        const toggleAction = (!trayOpen) ? linkLocations.HEADER_LINK_TRAYMENU_OPEN : linkLocations.HEADER_LINK_TRAYMENU_CLOSE;
        edapTrigger(namedEvents.HEADER_LINK_CLICK, {actionlocation: toggleAction});

        setTrayOpen(!trayOpen);
    }

    function updateLogoutUrl(travelerCopy) {
        if (!travelerCopy || !travelerCopy.items) {
            return;
        }

        let itemIndex;
        let item;

        travelerCopy.items.forEach((currentItem, i) => {
            /* istanbul ignore else */
            if (currentItem.cssClass === 'se-traveler-logout') {
                itemIndex = i;
                item = currentItem;
            }
        });

        if (!logoutRedirectUrl || !item) {
            return;
        }

        const serviceRE = /(^.*service=)(.*)/;

        // update traveler's logout link to use logoutUrl prop instead
        const newServiceParameter = encodeURIComponent(logoutRedirectUrl);
        item.link = item.link.replace(serviceRE, `$1${newServiceParameter}`);
        item.linkUpdated = true;
        travelerCopy.items[itemIndex] = item;
    }

    function getClassName() {
        return classNames(
            'site-header',
            isInverse && 'site-header--inverse',
            'border-border-color',
            theme || siteName,
            bgWhite && 'site-header--white'
        );
    }

    function getAccessibility() {
        if (withoutAccessibility) {
            return false;
        }
        return accessibility;
    }

    function getCountryCode() {
        return fullLocale && fullLocale.split('_')[1].toLowerCase();
    }

    function getCurrencyCode() {
        return currencyCode;
    }

    function getCurrencySelector() {
        return currencySelector;
    }


    function getFlags() {
        if (withoutFlags) {
            return false;
        }

        return flags;
    }

    function getHelp() {
        if (withoutHelpLinks && help) {
            let helpSlice = {...help};

            helpSlice.items = helpSlice.items.slice(0, 6);

            return helpSlice;
        }

        return help;
    }

    function getLanguageCode() {
        return fullLocale.substr(0, 5);
    }

    function getLogin() {
        if (withoutLogin) {
            return false;
        }

        if (login && travelerLoginClickHandler) {
            login.travelerLoginClickHandler = travelerLoginClickHandler;
        }

        return login;
    }

    function getScratchpad() {
        if (withoutScratchpad) {
            return false;
        }

        return scratchpad;
    }

    function getRoadTrips() {
        if (showRoadTrips && typeof roadTrips === 'object' && roadTrips !== null) {
            if (typeof clientRoadTrips === 'object' && clientRoadTrips !== null) {
                return {
                    ...roadTrips,
                    ...clientRoadTrips,
                };
            }
            return roadTrips;
        }

        return null;
    }

    function getTraveler() {
        if (withoutTraveler) {
            return false;
        }

        // defensive copy to protect props as updateLogoutUrl and determineAvatar are destructive
        const travelerCopy = typeof (traveler) !== 'undefined' ? JSON.parse(JSON.stringify(traveler)) : traveler;

        updateLogoutUrl(travelerCopy);

        return determineAvatar(travelerCopy);
    }

    function getTravelerInbox() {
        if (withoutInbox) {
            return false;
        }

        return travelerInbox;
    }

    function getTripboard() {
        return tripboard;
    }

    // eslint-disable-next-line complexity
    function getLocaleSwitcher() {
        if (withoutLocaleSwitcher || !localeSwitcher || !localeSwitcher.locales || localeSwitcher.locales.length <= 1) {
            return false;
        }

        let localeObjects = localeSwitcher.locales;

        if (localeSwitcherOverrides) {
            let localeObjsWithOverrides = localeObjects.map((localeObj) => {
                let matchedLocaleObj = localeSwitcherOverrides.find((override) => override.siteName === localeObj.siteName);
                if (matchedLocaleObj) {
                    return {...localeObj, ...matchedLocaleObj};
                }
                return localeObj;
            });
            return {...localeSwitcher, ...{locales: localeObjsWithOverrides}};
        }
        return localeSwitcher;
    }

    function getSignUp() {
        if (!withoutSignUp && displaySignUpLink && signUp && getLogin()) {
            return signUp;
        }
        return false;
    }

    const feedbackComponent = ({className = '', feedbackTabClassList = ''}) =>
        <Feedback className={className} feedbackTabClassList={feedbackTabClassList} lingua={feedbackComponentLingua} />;

    let siteHeaderTrayToggleClass = 'site-header__tray-toggle hidden-lg hidden-xl';
    let siteHeaderTrayToggleAriaLabel = 'Open site navigation';
    if (displayTrayJewel) {
        siteHeaderTrayToggleClass = ' display-jewel';
    }
    if (trayOpen) {
        siteHeaderTrayToggleClass += ' site-header__tray-toggle--close';
        siteHeaderTrayToggleAriaLabel = 'Close site navigation';
    }

    return (
        <div className="site-header--wrapper">
            <NotificationBar
                show={displayNotificationBar}
                onDismissMessage={handleNotificationMessageDismiss}
                onDismissBar={handleNotificationBarDismiss}
                messages={notificationMessages}
            />
            <div className={getClassName()}>
                <EdapProvider>
                    {logo && (
                        <Logo
                            {...logo}
                            hasSearch={hasSearch}
                            image={logo.image}
                            partnerLogo={partnerLogo}
                            sitePartnerLogo={sitePartnerLogo}
                            link={logo.link}
                        />
                    )}

                    {/* Render children items to support mixins like search experience */}
                    <div className="site-header__flex-spacer">
                        {children}
                    </div>

                    {!withoutNav && (
                        <Nav
                            accessibility={getAccessibility()}
                            countryCode={getCountryCode()}
                            currencyCode={getCurrencyCode()}
                            currencySelector={getCurrencySelector()}
                            extraNav={extraNav}
                            feedbackComponent={feedbackComponentLingua && feedbackComponent({className: 'site-header-nav__link site-header-nav__dropdown'})} // adding classnames of surrounding elements to feedback-component so it can inherit and fit in new context
                            flags={getFlags()}
                            globalizationSelector={globalizationSelector}
                            localeSwitcher={getLocaleSwitcher()}
                            help={getHelp()}
                            inbox={getTravelerInbox()}
                            internalTools={internalTools}
                            isInverse={isInverse}
                            isMobile={isMobile}
                            languageCode={getLanguageCode()}
                            login={getLogin()}
                            onGlobalSelectorChange={onGlobalSelectorChange}
                            renderCountryLanguageSelector={false}
                            renderCurrencySelector={!withoutCurrencySelector}
                            roadTrips={getRoadTrips()}
                            scratchpad={getScratchpad()}
                            signUp={getSignUp()}
                            tripboard={getTripboard()}
                            traveler={getTraveler()}
                        />
                    )}

                    {!withoutTray && (
                        // eslint-disable-next-line jsx-a11y/anchor-is-valid
                        <a className={siteHeaderTrayToggleClass}
                            onClick={handleToggleOpenTray}
                            onKeyPress={handleToggleOpenTray}
                            role="button"
                            tabIndex={0}
                            aria-label={siteHeaderTrayToggleAriaLabel}
                        >
                            <span>
                                <svg height="14" viewBox="0 0 18 14" width="18">
                                    <g className="rect-svg rect-svg--top" fillRule="nonzero">
                                        <rect className="rect-top" height="2" rx="1" width="18" />
                                    </g>
                                    <g className="rect-svg rect-svg--middle" fillRule="nonzero">
                                        <rect className="rect-middle" height="2" rx="1" width="18" y="6" />
                                    </g>
                                    <g className="rect-svg rect-svg--bottom" fillRule="nonzero">
                                        <rect className="rect-bottom" height="2" rx="1" width="18" y="12" />
                                    </g>
                                </svg>
                            </span>
                        </a>
                    )}

                    {!withoutTray && (
                        <Tray
                            countryCode={getCountryCode()}
                            currencyCode={getCurrencyCode()}
                            currencySelector={getCurrencySelector()}
                            extraNav={extraNav}
                            feedbackComponent={feedbackComponentLingua && feedbackComponent({className: 'site-header-tray__menu-item-link'})} // adding classnames of surrounding elements to feedback-component so it can inherit and fit in new context
                            flags={getFlags()}
                            globalizationSelector={globalizationSelector}
                            handleToggleOpen={handleToggleOpenTray}
                            help={getHelp()}
                            inbox={getTravelerInbox()}
                            inverse={isInverse}
                            isOpen={trayOpen}
                            isMobile={isMobile}
                            languageCode={getLanguageCode()}
                            login={getLogin()}
                            lyp={!withoutListYourProperty && lyp}
                            mobileApp={mobileApp}
                            onGlobalSelectorChange={onGlobalSelectorChange}
                            renderCountryLanguageSelector={false}
                            renderCurrencySelector={!withoutCurrencySelector}
                            roadTrips={getRoadTrips()}
                            signUp={getSignUp()}
                            scratchpad={getScratchpad()}
                            tripboard={getTripboard()}
                            traveler={getTraveler()}
                            localeSwitcher={getLocaleSwitcher()}
                        />
                    )}

                    {lyp && !withoutListYourProperty && (
                        <ListYourProperty
                            {...lyp}
                            isInverse={isInverse}
                        />
                    )}
                </EdapProvider>
            </div>
        </div>

    );
}

SiteHeader.defaultProps = {
    defaultAvatar: '//csvcus.homeaway.com/rsrcs/cdn-logos/3.0.0/bce/brand/misc/default-profile-pic.svg',
    displayNotificationBar: false,
    displayTrayJewel: false,
    features: {},
    fullLocale: 'en_US_HA',
    homeAwayWhiteLogoImage: '//csvcus.homeaway.com/rsrcs/cdn-logos/3.0.0/bce/moniker/homeaway_us/logo-bceheader-white.svg',
    isInverse: false,
    notificationMessages: [],
    onNotificationBarDismiss: () => { },
    onNotificationMessageDismiss: () => { },
    onTrayChange: () => { },
    showRoadTrips: false,
    withoutCurrencySelector: false,
    withoutLocaleSwitcher: true,
    vrboLogoImage: '//csvcus.homeaway.com/rsrcs/cdn-logos/3.0.0/bce/moniker/vrbo/logo.svg'
};

SiteHeader.propTypes = {
    bgWhite: PropTypes.bool,
    currencyCode: PropTypes.string,
    displayNotificationBar: PropTypes.bool,
    displaySignUpLink: PropTypes.bool,
    displayTrayJewel: PropTypes.bool,
    fullLocale: PropTypes.string,
    homeAwayWhiteLogoImage: PropTypes.string,
    inverseVrboLogoImage: PropTypes.string,
    isFlagsDisabled: PropTypes.bool,
    isInverse: PropTypes.bool,
    isTrayOpen: PropTypes.bool,
    logoutRedirectUrl: PropTypes.string,
    /**
     * Overrides default implementation of Global selector change handler. See docs for example of usage.
     */
    onGlobalSelectorChange: PropTypes.func,
    onTrayChange: PropTypes.func,
    onNotificationBarDismiss: PropTypes.func,
    onNotificationMessageDismiss: PropTypes.func,
    notificationMessages: PropTypes.arrayOf(PropTypes.node),
    showRoadTrips: PropTypes.bool,
    roadTrips: PropTypes.oneOfType([PropTypes.object]),
    clientRoadTrips: PropTypes.oneOfType([PropTypes.object]),
    theme: PropTypes.string,
    tripboard: PropTypes.oneOfType([PropTypes.node, PropTypes.element, PropTypes.func]),
    /**
         * Overrides default generated locale switcher link.
         * e.g. if current site is Vrbo and location is https://www.vrbo.com/vacation-rental/p123
         * The corresponding property on homeaway_ca site is https://www.vrbo.com/en-ca/cottage-rental/p123
         * Put the following to override default locale switcher redirection link
         * [{ locale: 'en_CA', link: 'https://www.vrbo.com/en-ca/cottage-rental/p123' }]
         */
    localeSwitcherOverrides: PropTypes.arrayOf(PropTypes.shape({
        locale: PropTypes.string,
        link: PropTypes.string,
    })),
    vrboLogoImage: PropTypes.string,
    withoutCurrencySelector: PropTypes.bool,
    withoutFlags: PropTypes.bool,
    withoutHelpLinks: PropTypes.bool,
    withoutInbox: PropTypes.bool,
    withoutListYourProperty: PropTypes.bool,
    withoutLogin: PropTypes.bool,
    withoutNav: PropTypes.bool,
    withoutScratchpad: PropTypes.bool,
    withoutSearch: PropTypes.bool,
    withoutSignUp: PropTypes.bool,
    withoutTraveler: PropTypes.bool,
    withoutTray: PropTypes.bool,
    withoutAccessibility: PropTypes.bool,
    withoutLocaleSwitcher: PropTypes.bool
};

export default determineIfMobile(SiteHeader, 1199);
